import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GetDashboardSummaryAPI from "../../../api/dashboard/DashboardController";
import DetailModalComponent from "../../../components/Modal/DetailModalComponent";

const DashboardPage = () => {
  const theme = useTheme();
  const defaultSummary = {
    totalProducts: 0,
    ordersToday: 0,
    activeCustomers: 0,
    revenueLast30Days: 0,
    recentOrders: [],
    inventoryAlerts: [],
  };
  const [summary, setSummary] = useState(defaultSummary);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("All");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    let isActive = true;
    const loadSummary = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const data = await GetDashboardSummaryAPI();
        if (!isActive) {
          return;
        }
        if (data?.baseResponseModel?.respCode === 200) {
          setSummary(data.summary || defaultSummary);
        } else {
          setLoadError(
            data?.baseResponseModel?.respMessage || "Failed to load dashboard data."
          );
        }
      } catch (error) {
        if (isActive) {
          setLoadError("Failed to load dashboard data.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadSummary();
    return () => {
      isActive = false;
    };
  }, []);

  const formatMMK = (value) => {
    if (!value || Number.isNaN(Number(value))) {
      return "MMK 0";
    }
    const amount = Number(value);
    if (amount >= 1_000_000) {
      return `MMK ${(amount / 1_000_000).toFixed(1)}M`;
    }
    if (amount >= 1_000) {
      return `MMK ${(amount / 1_000).toFixed(1)}K`;
    }
    return `MMK ${amount.toLocaleString()}`;
  };

  const formatMMKFull = (value) => {
    if (!value || Number.isNaN(Number(value))) {
      return "MMK 0";
    }
    return `MMK ${Number(value).toLocaleString()}`;
  };

  const getOrderTimestamp = (order) => {
    const rawValue =
      order?.orderDate ||
      order?.OrderDate ||
      order?.createdAt ||
      order?.CreatedAt ||
      order?.createdDate ||
      order?.CreatedDate ||
      order?.createDate ||
      order?.createDateTime ||
      order?.orderTime ||
      order?.createdOn;
    if (rawValue === undefined || rawValue === null) {
      return null;
    }
    const timestamp = new Date(rawValue).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
    if (typeof rawValue === "number") {
      return rawValue;
    }
    return null;
  };

  const kpiCards = useMemo(
    () => [
      {
        label: "Total Products",
        value: summary.totalProducts.toLocaleString(),
        subtext: "Live from inventory",
        icon: <Inventory2OutlinedIcon />,
      },
      {
        label: "Orders Today",
        value: summary.ordersToday.toLocaleString(),
        subtext: "Last 24 hours",
        icon: <LocalShippingOutlinedIcon />,
      },
      {
        label: "Active Customers",
        value: summary.activeCustomers.toLocaleString(),
        subtext: "Registered users",
        icon: <PeopleAltOutlinedIcon />,
      },
      {
        label: "Revenue (30d)",
        value: formatMMK(summary.revenueLast30Days),
        subtext: "Rolling 30 days",
        icon: <PaymentsOutlinedIcon />,
      },
    ],
    [summary]
  );

  const recentOrders = useMemo(
    () =>
      (() => {
        const orders = summary.recentOrders.slice();
        const hasTimestamp = orders.some(
          (order) => getOrderTimestamp(order) !== null
        );
        const compareOrderIds = (first, second) => {
          const firstId = String(first?.orderId || "");
          const secondId = String(second?.orderId || "");
          return secondId.localeCompare(firstId);
        };
        orders.sort((a, b) => {
          const aTimestamp = getOrderTimestamp(a);
          const bTimestamp = getOrderTimestamp(b);
          if (aTimestamp === null && bTimestamp === null) {
            return compareOrderIds(a, b);
          }
          if (aTimestamp === null) {
            return 1;
          }
          if (bTimestamp === null) {
            return -1;
          }
          if (bTimestamp === aTimestamp) {
            return compareOrderIds(a, b);
          }
          return bTimestamp - aTimestamp;
        });
        return orders.map((order) => ({
          id: order.orderId,
          name: order.customerName || "Customer",
          amount: formatMMKFull(order.amount),
          status: order.paymentMethod === "COD" ? "COD" : "Paid",
        }));
      })(),
    [summary.recentOrders]
  );

  const inventoryAlerts = useMemo(
    () =>
      summary.inventoryAlerts.map((item) => ({
        title: item.productTitle,
        note:
          item.quantity !== null && item.quantity !== undefined
            ? `Low stock (${item.quantity} left)`
            : "Restock soon",
      })),
    [summary.inventoryAlerts]
  );

  const filteredOrders = useMemo(() => {
    if (orderStatusFilter === "All") {
      return recentOrders;
    }
    return recentOrders.filter((order) => order.status === orderStatusFilter);
  }, [orderStatusFilter, recentOrders]);

  const statusChipStyle = (status) => {
    if (status === "Paid") {
      return { backgroundColor: "#E6F4EA", color: "#2E7D32" };
    }
    return { backgroundColor: "#FFF3E0", color: "#E65100" };
  };

  const handleRecentOrderClick = (order) => {
    setSelectedOrder({ orderId: order.id });
    setIsDetailOpen(true);
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
    setSelectedOrder(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Track products, orders, and customer activity at a glance.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} lg={3} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.action.main}`,
                background: theme.palette.secondary.main,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {card.value}
                  </Typography>
                </Box>
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 2 }}
              >
                {card.subtext}
              </Typography>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.action.main}`,
              background: "#FFFFFF",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent Orders
              </Typography>
              <Stack direction="row" spacing={1}>
                {["All", "Paid", "COD"].map((status) => (
                  <Chip
                    key={status}
                    size="small"
                    icon={status === "All" ? <TrendingUpOutlinedIcon /> : undefined}
                    label={status}
                    variant={orderStatusFilter === status ? "filled" : "outlined"}
                    onClick={() => setOrderStatusFilter(status)}
                    sx={
                      orderStatusFilter === status
                        ? {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.text.main,
                          }
                        : { borderColor: theme.palette.action.main }
                    }
                  />
                ))}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              {isLoading && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Loading recent orders...
                </Typography>
              )}
              {!isLoading && loadError && (
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  {loadError}
                </Typography>
              )}
              {!isLoading && !loadError && filteredOrders.length === 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No recent orders found.
                </Typography>
              )}
              {!isLoading &&
                !loadError &&
                filteredOrders.map((order) => (
                  <Box
                    key={order.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                      cursor: "pointer",
                      borderRadius: 1,
                      p: 1,
                      "&:hover": { backgroundColor: theme.palette.secondary.main },
                    }}
                    onClick={() => handleRecentOrderClick(order)}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {order.id}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {order.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "left",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {order.amount}
                    </Typography>
                    <Chip
                      size="small"
                      label={order.status}
                      sx={statusChipStyle(order.status)}
                    />
                  </Box>
                ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.action.main}`,
              background: "#FFFFFF",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Inventory Alerts
              </Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              {isLoading && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Loading inventory alerts...
                </Typography>
              )}
              {!isLoading && loadError && (
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  {loadError}
                </Typography>
              )}
              {!isLoading && !loadError && inventoryAlerts.length === 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No inventory alerts.
                </Typography>
              )}
              {!isLoading &&
                !loadError &&
                inventoryAlerts.map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.secondary.main,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {item.note}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <DetailModalComponent
        open={isDetailOpen}
        onClose={handleDetailClose}
        data={selectedOrder}
        category="Order"
      />
    </Box>
  );
};

export default DashboardPage;
